/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { Ionicons } from '@expo/vector-icons';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives';
import { matchesSearchText } from '../../shared/format/searchText';
import { spacing, ticketColors, ticketFonts } from '../../shared/theme/public-api';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import type { CategoryOption } from '../domain/categoryOptions';

type Props = {
  label: string;
  pickerLabel: string;
  sheetTitle: string;
  categoryOptions: CategoryOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  accessibilityLabel: string;
};

export function BulkCategoryMultiPicker({
  label,
  pickerLabel,
  sheetTitle,
  categoryOptions,
  selectedIds,
  onChange,
  accessibilityLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string[]>([]);
  const insets = useSafeAreaInsets();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const wide = screenWidth >= 768;

  const options = useMemo(
    () =>
      categoryOptions
        .filter((o) => o.id !== 'all')
        .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
    [categoryOptions],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    return options.filter((o) => matchesSearchText(o.label, query));
  }, [options, query]);

  useEffect(() => {
    if (open) {
      setDraft([...selectedIds]);
      setQuery('');
    }
  }, [open, selectedIds]);

  const valueLabel = useMemo(() => {
    if (selectedIds.length === 0) return pickerLabel;
    const names = selectedIds
      .map((id) => options.find((o) => o.id === id)?.label)
      .filter(Boolean) as string[];
    if (names.length === 1) return names[0]!;
    if (names.length === 2) return `${names[0]} y ${names[1]}`;
    return `${names.length} categorías`;
  }, [options, pickerLabel, selectedIds]);

  const toggleDraft = (id: string) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const applyDraft = () => {
    onChange(draft);
    setOpen(false);
  };

  const sheet = (
    <View style={[styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm), maxHeight: screenHeight * 0.88 }]}>
      {!wide ? <View style={styles.grabber} /> : null}
      <TicketModalHeader title={sheetTitle} onClose={() => setOpen(false)} compact />
      <TextInput
        style={styles.search}
        value={query}
        onChangeText={setQuery}
        placeholder="Buscar…"
        placeholderTextColor={ticketColors.ink3}
        autoCapitalize="words"
      />
      <ScrollView keyboardShouldPersistTaps="handled" style={styles.list}>
        {filtered.map((opt) => {
          const active = draft.includes(opt.id);
          return (
            <TouchableOpacity
              key={opt.id}
              style={[styles.row, active && styles.rowActive]}
              onPress={() => toggleDraft(opt.id)}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: active }}
            >
              <Ionicons
                name={active ? 'checkbox' : 'square-outline'}
                size={22}
                color={active ? ticketColors.gold : ticketColors.ink3}
              />
              <Text style={styles.rowLabel} numberOfLines={2}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      <TouchableOpacity
        style={[styles.applyBtn, draft.length === 0 && styles.applyBtnDisabled]}
        onPress={applyDraft}
        disabled={draft.length === 0}
        accessibilityRole="button"
      >
        <Text style={styles.applyBtnText}>Listo</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <>
      <OperationalFilterPickerRow
        layout="stack"
        label={label}
        valueLabel={valueLabel}
        active={selectedIds.length > 0}
        onPress={() => setOpen(true)}
        accessibilityLabel={accessibilityLabel}
      />
      <Modal visible={open} transparent animationType={wide ? 'fade' : 'slide'} onRequestClose={() => setOpen(false)}>
        {wide ? (
          <View style={styles.wideRoot}>
            <Pressable style={StyleSheet.absoluteFill} onPress={() => setOpen(false)} />
            <View style={[styles.dialog, { maxWidth: Math.min(440, screenWidth - 32) }]}>{sheet}</View>
          </View>
        ) : (
          <View style={styles.backdrop}>{sheet}</View>
        )}
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  wideRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  dialog: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    overflow: 'hidden',
    zIndex: 1,
    width: '100%',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 18,
  },
  grabber: {
    alignSelf: 'center',
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.ruleSoft,
    marginTop: 8,
    marginBottom: 8,
  },
  search: {
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: 12,
    fontSize: 14,
    color: ticketColors.ink,
    marginBottom: 8,
  },
  list: {
    flexGrow: 0,
    maxHeight: 360,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  rowActive: {
    backgroundColor: ticketColors.paper,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  applyBtn: {
    marginTop: 12,
    marginBottom: 8,
    minHeight: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  applyBtnDisabled: {
    opacity: 0.45,
  },
  applyBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.paper,
  },
});
